import { useEffect, useId, useRef, useState } from 'react'
import Icon from './Icon'
import Cased from '@/components/ui/Cased'

export function MissionDashboard({ compact = false }: { compact?: boolean }) {
  const [period, setPeriod] = useState('This week')
  const Heading = compact ? 'h2' : 'h3'
  const current = period === 'This week'
  const metrics = current ? [24, 18, 6] : [92, 74, 18]
  return (
    <div className={`pat-dashboard${compact ? ' pat-dashboard--compact' : ''}`}>
      <div className="pat-appbar">
        <span className="pat-appmark">
          <Icon name="layers" size={15} /> MISSION OPERATIONS
        </span>
        <span className="pat-avatar" aria-label="Example workspace">
          M
        </span>
      </div>
      <div className="pat-dashboard__body">
        <div className="pat-title-row">
          <div>
            <span className="mds-eyebrow">WORKSPACE OVERVIEW</span>
            <Heading>Ready for what&apos;s next.</Heading>
          </div>
          <span className="mds-tag">
            <span className="mds-tag__dot pat-dot--success" aria-hidden="true" /> On track
          </span>
        </div>
        <div
          className="mds-segmented"
          role="group"
          aria-label="Reporting period"
        >
          {['This week', 'This month'].map((option) => (
            <button
              key={option}
              type="button"
              className="mds-segmented__option"
              aria-pressed={period === option}
              onClick={() => setPeriod(option)}
            >
              {option}
            </button>
          ))}
        </div>
        <div className="pat-metrics" aria-live="polite">
          {['Active missions', 'Milestones complete', 'Needs attention'].map(
            (label, index) => (
              <div className="pat-metric" key={label}>
                <span>{label}</span>
                <strong>{metrics[index]}</strong>
                <small>{index === 2 ? 'Ready for review' : period}</small>
              </div>
            ),
          )}
        </div>
        <div className="pat-chart">
          <div>
            <strong>Mission progress</strong>
            <span>{period}</span>
          </div>
          <div
            className="pat-chart__bars"
            aria-label={`Mission progress, ${period.toLowerCase()}: Planning 100%, Delivery ${current ? 72 : 84}%, Review ${current ? 48 : 62}%`}
          >
            {[
              ['Planning', 100],
              ['Delivery', current ? 72 : 84],
              ['Review', current ? 48 : 62],
            ].map(([label, value]) => (
              <div className="pat-chart__row" key={label}>
                <span>{label}</span>
                <div className="pat-chart__track">
                  <div style={{ width: `${value}%` }} />
                </div>
                <strong>{value}%</strong>
              </div>
            ))}
          </div>
        </div>
        <div className="pat-next">
          <div className="pat-next__icon">
            <Icon name="check" size={16} />
          </div>
          <div>
            <strong>Your next move</strong>
            <span>Review the regional readiness brief</span>
          </div>
          <Icon name="arrow" size={18} />
        </div>
      </div>
    </div>
  )
}

const initialRecords = [
  {
    name: 'Coastal readiness plan',
    team: 'Field operations',
    status: 'Ready for review',
  },
  {
    name: 'Regional equipment request',
    team: 'Mission support',
    status: 'In progress',
  },
  {
    name: 'Training coordination brief',
    team: 'Workforce',
    status: 'Ready for review',
  },
]
function ReviewQueue() {
  const id = useId()
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('All statuses')
  const [records, setRecords] = useState(initialRecords)
  const [notice, setNotice] = useState('')
  const searchInput = useRef<HTMLInputElement>(null)
  const filtered = records.filter(
    (record) =>
      record.name.toLowerCase().includes(query.toLowerCase()) &&
      (filter === 'All statuses' || record.status === filter),
  )
  return (
    <div className="pat-pad">
      <div className="pat-title-row">
        <h3><Cased>Review workspace</Cased></h3>
        <span className="mds-tag mds-tag--outline">Illustrative records</span>
      </div>
      <div className="pat-field-row">
        <div className="mds-field">
          <label className="mds-label" htmlFor={`${id}-search`}>Find a request</label>
          <input
            ref={searchInput}
            className="mds-input"
            id={`${id}-search`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by title"
          />
        </div>
        <div className="mds-field">
          <label className="mds-label" htmlFor={`${id}-status`}>Status</label>
          <select
            className="mds-select"
            id={`${id}-status`}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          >
            {[
              'All statuses',
              'Ready for review',
              'In progress',
              'Reviewed',
            ].map((status) => (
              <option key={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>
      <p className="pat-status" role="status">
        {filtered.length} {filtered.length === 1 ? 'request' : 'requests'} shown
        {notice ? `. ${notice}` : ''}
      </p>
      <ul className="pat-queue">
        {filtered.map((record) => (
          <li key={record.name}>
            <div>
              <strong>{record.name}</strong>
              <span>{record.team}</span>
            </div>
            <div>
              <span className="mds-tag mds-tag--outline">{record.status}</span>
              {record.status !== 'Reviewed' && (
                <button
                  className="mds-button mds-button--ghost mds-button--sm"
                  onClick={() => {
                    setRecords(
                      records.map((item) =>
                        item.name === record.name
                          ? { ...item, status: 'Reviewed' }
                          : item,
                      ),
                    )
                    setNotice(`${record.name} marked reviewed`)
                    searchInput.current?.focus()
                  }}
                >
                  Mark reviewed <span className="mds-visually-hidden">({record.name})</span>{' '}
                  <Icon name="check" size={15} />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {filtered.length === 0 && (
        <div className="pat-empty">
          <h4>No requests match</h4>
          <p>Try a different title or clear your filters.</p>
          <button
            className="mds-button mds-button--secondary"
            onClick={() => {
              setQuery('')
              setFilter('All statuses')
              searchInput.current?.focus()
            }}
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  )
}
function GuidedIntake() {
  const id = useId()
  const [step, setStep] = useState(1)
  const [title, setTitle] = useState('')
  const [priority, setPriority] = useState('Standard')
  const [error, setError] = useState(false)
  const titleInput = useRef<HTMLInputElement>(null)
  const stepHeading = useRef<HTMLHeadingElement>(null)
  const previousStep = useRef(step)
  useEffect(() => {
    if (previousStep.current === step) return
    previousStep.current = step
    if (step === 1) titleInput.current?.focus()
    else stepHeading.current?.focus()
  }, [step])
  return (
    <div className="pat-pad">
      <div className="pat-title-row">
        <h3><Cased>Create a mission request</Cased></h3>
        <span className="mds-tag mds-tag--outline">Example only</span>
      </div>
      <ol className="pat-steps" aria-label="Request progress">
        {['Details', 'Review', 'Complete'].map((name, index) => (
          <li key={name} aria-current={step === index + 1 ? 'step' : undefined}>
            <span>
              {step > index + 1 ? <Icon name="check" size={13} /> : index + 1}
            </span>
            {name}
          </li>
        ))}
      </ol>
      {step === 1 ? (
        <form
          className="pat-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            if (!title.trim()) {
              setError(true)
              titleInput.current?.focus()
            } else {
              setError(false)
              setStep(2)
            }
          }}
        >
          <div className="mds-field">
            <label className="mds-label" htmlFor={`${id}-title`}>
              Request title <span className="mds-muted">(required)</span>
            </label>
            <p id={`${id}-help`} className="mds-hint">
              Use an illustrative title. This example keeps input in this page
              only.
            </p>
            <input
              ref={titleInput}
              className="mds-input"
              id={`${id}-title`}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              aria-invalid={error || undefined}
              aria-describedby={`${id}-help${error ? ` ${id}-error` : ''}`}
            />
            {error && (
              <p className="mds-error-text" id={`${id}-error`}>
                Enter a request title to continue.
              </p>
            )}
          </div>
          <div className="mds-field">
            <label className="mds-label" htmlFor={`${id}-priority`}>Priority</label>
            <select
              className="mds-select"
              id={`${id}-priority`}
              value={priority}
              onChange={(event) => setPriority(event.target.value)}
            >
              <option>Standard</option>
              <option>Time-sensitive</option>
            </select>
          </div>
          <button className="mds-button mds-button--primary" type="submit">
            Review request <Icon name="arrow" size={16} />
          </button>
        </form>
      ) : step === 2 ? (
        <div className="pat-review">
          <h4 ref={stepHeading} tabIndex={-1} className="mds-h3">
            Review your details
          </h4>
          <dl className="pat-details">
            <div>
              <dt>Request title</dt>
              <dd>{title}</dd>
            </div>
            <div>
              <dt>Priority</dt>
              <dd>{priority}</dd>
            </div>
          </dl>
          <div className="mds-cluster">
            <button
              className="mds-button mds-button--primary"
              onClick={() => setStep(3)}
            >
              Confirm example request <Icon name="check" size={16} />
            </button>
            <button
              className="mds-button mds-button--secondary"
              onClick={() => setStep(1)}
            >
              Edit details
            </button>
          </div>
        </div>
      ) : (
        <div className="pat-confirmation" role="status">
          <span className="pat-confirmation__icon">
            <Icon name="check" />
          </span>
          <h4 ref={stepHeading} tabIndex={-1}>
            Example request complete
          </h4>
          <p>
            {title} is ready for its next review. This interaction is contained
            in your browser.
          </p>
          <button
            className="mds-button mds-button--secondary"
            onClick={() => {
              setTitle('')
              setPriority('Standard')
              setStep(1)
            }}
          >
            Start another request
          </button>
        </div>
      )}
    </div>
  )
}
function RecordDetail() {
  const [confirming, setConfirming] = useState(false)
  const [complete, setComplete] = useState(false)
  const recordHeading = useRef<HTMLHeadingElement>(null)
  const confirmationHeading = useRef<HTMLHeadingElement>(null)
  const reviewButton = useRef<HTMLButtonElement>(null)
  const previousState = useRef({ confirming, complete })
  useEffect(() => {
    if (
      previousState.current.confirming === confirming &&
      previousState.current.complete === complete
    )
      return
    previousState.current = { confirming, complete }
    if (complete) recordHeading.current?.focus()
    else if (confirming) confirmationHeading.current?.focus()
    else reviewButton.current?.focus()
  }, [confirming, complete])
  return (
    <div className="pat-pad">
      <span className="mds-eyebrow">MISSION RECORD / ILLUSTRATIVE DATA</span>
      <div className="pat-title-row">
        <h3 ref={recordHeading} tabIndex={-1}>
          <Cased>Regional readiness brief</Cased>
        </h3>
        <span className="mds-tag">
          <span className={`mds-tag__dot ${complete ? 'pat-dot--success' : 'pat-dot--info'}`} aria-hidden="true" />
          {complete ? 'Complete' : 'In review'}
        </span>
      </div>
      <dl className="pat-details">
        <div>
          <dt>Mission team</dt>
          <dd>Field operations</dd>
        </div>
        <div>
          <dt>Priority</dt>
          <dd>Standard</dd>
        </div>
        <div>
          <dt>Next action</dt>
          <dd>
            {complete
              ? 'Review the activity history'
              : 'Confirm the review is complete'}
          </dd>
        </div>
      </dl>
      <h4 className="pat-subhead">Activity</h4>
      <ul className="pat-activity">
        <li>Brief prepared for review</li>
        <li>Supporting information checked</li>
        {complete && <li>Review marked complete</li>}
      </ul>
      {confirming ? (
        <div className="pat-confirm">
          <h4 ref={confirmationHeading} tabIndex={-1}>
            Mark this review complete?
          </h4>
          <p>
            The record status and activity history will update in this example.
          </p>
          <div className="mds-cluster">
            <button
              className="mds-button mds-button--primary"
              onClick={() => {
                setComplete(true)
                setConfirming(false)
              }}
            >
              Confirm completion
            </button>
            <button
              className="mds-button mds-button--secondary"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          className="mds-button mds-button--secondary"
          ref={reviewButton}
          disabled={complete}
          onClick={() => setConfirming(true)}
        >
          {complete ? 'Review complete' : 'Complete review'}
        </button>
      )}
      <span className="mds-visually-hidden" role="status">
        {complete ? 'Regional readiness brief review marked complete' : ''}
      </span>
    </div>
  )
}
function CitedAnswer() {
  const [feedback, setFeedback] = useState('')
  return (
    <div className="pat-pad">
      <span className="mds-eyebrow">SOURCE-GROUNDED ANSWER</span>
      <h3><Cased>What makes a status accessible?</Cased></h3>
      <div className="pat-answer">
        <p>
          Use a readable text label alongside the status color. WCAG&apos;s Use
          of Color criterion requires a visual cue beyond color alone to convey
          information.{' '}
          <a
            href="https://www.w3.org/WAI/WCAG21/Understanding/use-of-color.html"
            aria-label="Source 1: Understanding Use of Color"
          >
            [1]
          </a>
        </p>
        <p className="pat-answer__suggestion">
          <strong>Suggested application:</strong> pair a green indicator with
          the words “On track” so the meaning remains clear.
        </p>
        <div className="pat-answer__source">
          <Icon name="file" size={17} />
          <div>
            <strong>1. Understanding Use of Color</strong>
            <a href="https://www.w3.org/WAI/WCAG21/Understanding/use-of-color.html">
              W3C Web Accessibility Initiative{' '}
              <Icon name="external" size={12} />
            </a>
          </div>
        </div>
      </div>
      <div className="pat-feedback">
        <span>Was this explanation useful?</span>
        {['Helpful', 'Needs more detail'].map((value) => (
          <button
            className="mds-button mds-button--secondary"
            aria-pressed={feedback === value}
            key={value}
            onClick={() => setFeedback(value)}
          >
            {value}
          </button>
        ))}
      </div>
      <p role="status" className="pat-status">
        {feedback
          ? `Example feedback selected: ${feedback}.`
          : 'Feedback stays in this example.'}
      </p>
    </div>
  )
}
function EmptyState() {
  const [state, setState] = useState('No results')
  const [recovered, setRecovered] = useState(false)
  return (
    <div className="pat-pad">
      <div className="pat-title-row">
        <h3><Cased>Recovery states</Cased></h3>
        <div
          className="mds-segmented"
          role="group"
          aria-label="Example state"
        >
          {['No results', 'Service unavailable'].map((option) => (
            <button
              key={option}
              type="button"
              className="mds-segmented__option"
              aria-pressed={state === option}
              onClick={() => {
                setState(option)
                setRecovered(false)
              }}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div className="pat-empty" role="status">
        <span className="pat-empty__icon">
          <Icon
            name={
              recovered ? 'check' : state === 'No results' ? 'search' : 'clock'
            }
            size={30}
          />
        </span>
        <h4>
          {recovered
            ? 'You’re ready to continue'
            : state === 'No results'
              ? 'No missions match your search'
              : 'The mission list couldn’t load'}
        </h4>
        <p>
          {recovered
            ? 'Your example workspace is available again.'
            : state === 'No results'
              ? 'Try a broader search or clear the current filters to see all missions.'
              : 'Your work is still here. Try loading the list again.'}
        </p>
        <button
          className="mds-button mds-button--secondary"
          onClick={() => setRecovered(!recovered)}
        >
          {recovered
            ? 'Reset example'
            : state === 'No results'
              ? 'Clear filters'
              : 'Try again'}
        </button>
      </div>
    </div>
  )
}
export default function PatternExample({ id }: { id: string }) {
  switch (id) {
    case 'mission-dashboard':
      return <MissionDashboard />
    case 'review-queue':
      return <ReviewQueue />
    case 'guided-intake':
      return <GuidedIntake />
    case 'record-detail':
      return <RecordDetail />
    case 'cited-answer':
      return <CitedAnswer />
    default:
      return <EmptyState />
  }
}
